/**
 * UTM Parameter Preservation Utility
 * Ensures tracking parameters are preserved across navigation
 */

const PRESERVED_PARAMS = [
  'utm_source',
  'utm_medium', 
  'utm_campaign',
  'utm_term',
  'utm_content',
  'partner_id',
  'ref',
  'affiliate_code',
  'demo_role',
];

/**
 * Preserves UTM and tracking parameters when navigating to a new URL
 * @param url - Target URL (can be relative or absolute)
 * @returns URL with preserved parameters appended
 */
export const preserveParams = (url: string): string => {
  if (typeof window === 'undefined') return url;
  
  try {
    const currentParams = new URLSearchParams(window.location.search);
    const newUrl = new URL(url, window.location.origin);
    
    PRESERVED_PARAMS.forEach(key => {
      const value = currentParams.get(key);
      if (value && !newUrl.searchParams.has(key)) {
        newUrl.searchParams.set(key, value);
      }
    });
    
    // Return relative path if same origin
    if (newUrl.origin === window.location.origin) {
      return newUrl.pathname + newUrl.search + newUrl.hash;
    }
    
    return newUrl.toString();
  } catch {
    return url;
  }
};

/**
 * Gets current UTM parameters as an object
 * Useful for analytics and tracking
 */
export const getCurrentUTMParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};
  
  PRESERVED_PARAMS.forEach(key => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });
  
  return utmParams;
};

/**
 * Builds a URL with tracking parameters
 * @param baseUrl - Base URL to build upon
 * @param additionalParams - Additional params to add
 */
export const buildTrackedUrl = (
  baseUrl: string,
  additionalParams?: Record<string, string>
): string => {
  const preserved = preserveParams(baseUrl);
  
  if (!additionalParams) return preserved;
  
  try {
    const url = new URL(preserved, window.location.origin);
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });
    
    if (url.origin === window.location.origin) {
      return url.pathname + url.search + url.hash;
    }
    
    return url.toString();
  } catch {
    return preserved;
  }
};

export default preserveParams;
